import { useState } from 'react';
import { CONNECTION_TYPE } from '../constants';
import {
	initiateConnection,
	handleOAuthFlow,
	handlePlugAndPlayFlow,
	submitPlugAndPlayForm,
	getRedirectUrl,
	getErrorMessage
} from '../services/connectionFlows';
import { extractSessionId, resetEditState } from '../utils/shopUtils';

/**
 * Orchestrates POS connection flow and provides action handlers
 * Handles connection submissions, edit operations, and state management
 *
 * @param {Object} currentShop - Current shop object
 * @param {Object} stockConnection - Stock connection state from useConnectionState
 * @param {Object} productConnection - Product connection state from useConnectionState
 * @param {Function} onShopRefresh - Callback to refresh shop data
 * @returns {Object} Connection manager state and handlers
 */
export const usePOSConnectionManager = (currentShop, stockConnection, productConnection, onShopRefresh) => {
	const [posLoading, setPosLoading] = useState(false);
	const [error, setError] = useState(null);
	const [includeProductData, setIncludeProductData] = useState(false);

	const submitExistingPlugAndPlayConnection = async (connection, isStock, selectedProvider, formData) => {
		try {
			await submitPlugAndPlayForm({
				sessionId: connection.sessionId,
				formData,
				provider: selectedProvider,
				shopId: currentShop.id
			});

			if (isStock && includeProductData && productConnection.sessionId && selectedProvider?.kinds?.includes('product')) {
				await submitPlugAndPlayForm({
					sessionId: productConnection.sessionId,
					formData,
					provider: selectedProvider,
					shopId: currentShop.id
				});
			}

			if (onShopRefresh) {
				await onShopRefresh();
			}

			console.log('[submitExistingPlugAndPlayConnection] Setting polling to false and closing edit');
			connection.setPollingForConnection(false);
			setPosLoading(false);
			resetEditState(stockConnection, productConnection);
			console.log('[submitExistingPlugAndPlayConnection] Completed successfully');
		} catch (err) {
			console.error('[submitExistingPlugAndPlayConnection] Error:', err);
			setError(getErrorMessage(err));
			setPosLoading(false);
		}
	};

	const handleConnectPOS = async (kind = 'stock') => {
		const isStock = kind === 'stock' || (!productConnection.editing && !kind);
		const connection = isStock ? stockConnection : productConnection;
		const { selectedPOS, provider: selectedProvider, formData, formErrors, sessionId } = connection;

		console.log('[handleConnectPOS] Starting', {
			kind,
			isStock,
			selectedPOS,
			providerType: selectedProvider?.connection,
			submitType: selectedProvider?.connect?.submitType,
			hasSessionId: !!sessionId,
			pollingForConnection: connection.pollingForConnection
		});

		if (!selectedPOS || !currentShop) {
			setError('Please select a POS system.');
			return;
		}

		if (Object.keys(formErrors).length > 0) {
			setError('Please fix the errors in the form before submitting.');
			return;
		}

		setError(null);
		setPosLoading(true);

		try {
			// If we already have a session for a plug-and-play connection, submit it
			if (sessionId && selectedProvider?.connection === CONNECTION_TYPE.PLUG_PLAY) {
				const isSpinnerType = selectedProvider?.connect?.submitType === 'spinner';

				// Submit the form for ALL connector types
				await submitPlugAndPlayForm({
					sessionId: connection.sessionId,
					formData,
					provider: selectedProvider,
					shopId: currentShop.id
				});

				if (isStock && includeProductData && productConnection.sessionId && selectedProvider?.kinds?.includes('product')) {
					await submitPlugAndPlayForm({
						sessionId: productConnection.sessionId,
						formData,
						provider: selectedProvider,
						shopId: currentShop.id
					});
				}

				// Refresh shop data to get the updated connection
				if (onShopRefresh) {
					await onShopRefresh();
				}

				// For spinner types, start polling in background after closing edit
				if (isSpinnerType) {
					connection.setPollingForConnection(true);
				} else {
					connection.setPollingForConnection(false);
				}

				// Close edit panel for all types
				setPosLoading(false);
				resetEditState(stockConnection, productConnection);
				return;
			}

			// For plug-and-play connections without a session yet (e.g., when auto-session creation hasn't completed),
			// create the session now before submitting
			if (
				!sessionId &&
				selectedProvider?.connection === CONNECTION_TYPE.PLUG_PLAY &&
				selectedProvider?.connect?.submitType !== 'spinner'
			) {
				const redirectUrl = getRedirectUrl();
				const response = await initiateConnection({
					shopId: currentShop.id,
					provider: selectedPOS,
					kind: isStock ? 'stock' : 'product',
					redirectUrl
				});

				const newSessionId = extractSessionId(response);
				if (newSessionId) {
					connection.updateSessionId(newSessionId);
					// Ensure polling is off for manual connectors
					connection.setPollingForConnection(false);
					// Now submit the form with the newly created session

					await submitExistingPlugAndPlayConnection(connection, isStock, selectedProvider, formData);
					return;
				} else {
					// If session creation failed, show error and stop
					console.error('[handleConnectPOS] Session creation failed');
					setError('Failed to create connection session. Please try again.');
					setPosLoading(false);
					return;
				}
			}

			const redirectUrl = getRedirectUrl();
			const response = await initiateConnection({
				shopId: currentShop.id,
				provider: selectedPOS,
				kind: isStock ? 'stock' : 'product',
				redirectUrl
			});

			let productResponse = null;
			if (isStock && includeProductData && selectedProvider?.kinds?.includes('product')) {
				productResponse = await initiateConnection({
					shopId: currentShop.id,
					provider: selectedPOS,
					kind: 'product',
					redirectUrl
				});
			}

			const sessionIdValue = extractSessionId(response);

			if (sessionIdValue) {
				if (selectedProvider?.connection === CONNECTION_TYPE.OAUTH) {
					connection.updateSessionId(sessionIdValue);

					if (productResponse?.finishSetupLink) {
						const productSessionIdValue = extractSessionId(productResponse);
						productConnection.updateSessionId(productSessionIdValue);
					}

					handleOAuthFlow(connection, response, sessionIdValue);
					return;
				}

				if (selectedProvider?.connection === CONNECTION_TYPE.PLUG_PLAY) {
					connection.updateSessionId(sessionIdValue);

					if (productResponse?.finishSetupLink) {
						const productSessionIdValue = extractSessionId(productResponse);
						productConnection.updateSessionId(productSessionIdValue);
					}

					handlePlugAndPlayFlow(connection, selectedProvider, sessionIdValue);

					if (onShopRefresh) {
						await onShopRefresh();
					}

					setPosLoading(false);
					resetEditState(stockConnection, productConnection);
					return;
				}
			}

			if (onShopRefresh) {
				await onShopRefresh();
			}
			setPosLoading(false);
			resetEditState(stockConnection, productConnection);
		} catch (err) {
			setError(getErrorMessage(err));
			setPosLoading(false);
		}
	};

	const handleEditStock = () => {
		stockConnection.setEditing(true);
		stockConnection.setEditingPOS(stockConnection.source?.pointOfSale || '');
		stockConnection.setConnectionWarning(null);
		productConnection.setEditing(false);
		setPosLoading(false);
		setError(null);
	};

	const handleEditProduct = () => {
		productConnection.setEditing(true);
		productConnection.setEditingPOS(productConnection.source?.pointOfSale || '');
		productConnection.setConnectionWarning(null);
		stockConnection.setEditing(false);
		setPosLoading(false);
		setError(null);
	};

	const handleCloseEdit = () => {
		stockConnection.setEditing(false);
		stockConnection.setEditingPOS('');
		stockConnection.setConnectionWarning(null);
		productConnection.setEditing(false);
		productConnection.setEditingPOS('');
		productConnection.setConnectionWarning(null);
		setError(null);
	};

	return {
		posLoading,
		error,
		includeProductData,
		handleConnectPOS,
		handleEditStock,
		handleEditProduct,
		handleCloseEdit,
		setIncludeProductData,
		setError,
		setPosLoading
	};
};
